export const EMPTY_TOURNAMENT_PRIZES = {
  first: "",
  second: "",
  third: "",
};

export const PRIZE_FULFILLMENT_MODES = {
  REQUEST: "REQUEST",
};

export const STRUCTURED_PRIZE_TYPES = {
  PAYOUT: "PAYOUT",
  PHYSICAL: "PHYSICAL",
};

export const EMPTY_PLACEMENT_PRIZE = {
  enabled: false,
  type: STRUCTURED_PRIZE_TYPES.PAYOUT,
  amount: "",
  currency: "EUR",
  name: "",
  description: "",
};

const SUPPORTED_STRUCTURED_PLACEMENTS = ["1", "2", "3", "4"];

export function normalizePrizeText(value) {
  return String(value || "").trim();
}

export function getTournamentPrizeDrafts(source = {}) {
  const rawPrizes = source?.prizes && typeof source.prizes === "object" ? source.prizes : {};
  const first = String(
    rawPrizes.first ??
      rawPrizes["1"] ??
      source.prizeText ??
      source.prize ??
      source.rewardText ??
      ""
  );
  const second = String(
    rawPrizes.second ??
      rawPrizes["2"] ??
      source.secondPrize ??
      source.prizeSecond ??
      ""
  );
  const third = String(
    rawPrizes.third ??
      rawPrizes["3"] ??
      source.thirdPrize ??
      source.prizeThird ??
      ""
  );

  return { first, second, third };
}

export function normalizeTournamentPrizes(source = {}) {
  const drafts = getTournamentPrizeDrafts(source);
  return {
    first: normalizePrizeText(drafts.first),
    second: normalizePrizeText(drafts.second),
    third: normalizePrizeText(drafts.third),
  };
}

export function buildPrizePayload(prizes, { thirdPlaceEnabled = false } = {}) {
  const normalized = normalizeTournamentPrizes({ prizes });
  return {
    first: normalized.first,
    second: normalized.second,
    third: thirdPlaceEnabled ? normalized.third : "",
  };
}

export function hasAnyAdditionalPrize(source = {}) {
  const prizes = normalizeTournamentPrizes(source);
  return Boolean(prizes.second || prizes.third);
}

export function createEmptyPlacementPrizes({ thirdPlaceEnabled = false, fourthPlaceEnabled = false } = {}) {
  return SUPPORTED_STRUCTURED_PLACEMENTS.reduce((acc, placement) => {
    const enabledByDefault = placement === "1";
    const allowed =
      placement === "1" ||
      placement === "2" ||
      (placement === "3" && thirdPlaceEnabled) ||
      (placement === "4" && thirdPlaceEnabled && fourthPlaceEnabled);
    acc[placement] = {
      ...EMPTY_PLACEMENT_PRIZE,
      enabled: enabledByDefault && allowed,
    };
    return acc;
  }, {});
}

export function normalizePlacementPrizeDrafts(source = {}, options = {}) {
  const base = createEmptyPlacementPrizes(options);
  const raw = source?.placementPrizes && typeof source.placementPrizes === "object"
    ? source.placementPrizes
    : source;

  SUPPORTED_STRUCTURED_PLACEMENTS.forEach((placement) => {
    const prize = raw?.[placement];
    if (!prize || typeof prize !== "object") return;
    base[placement] = {
      ...base[placement],
      enabled: prize.enabled !== false,
      type: String(prize.type || STRUCTURED_PRIZE_TYPES.PAYOUT).trim().toUpperCase(),
      amount: prize.amount === null || prize.amount === undefined ? "" : String(prize.amount),
      currency: String(prize.currency || "EUR").trim().toUpperCase() || "EUR",
      name: String(prize.name || prize.physicalName || "").trim(),
      description: String(prize.description || "").trim(),
    };
  });

  if (!options.thirdPlaceEnabled) {
    base["3"] = { ...base["3"], enabled: false };
    base["4"] = { ...base["4"], enabled: false };
  }
  if (!options.fourthPlaceEnabled) {
    base["4"] = { ...base["4"], enabled: false };
  }

  return base;
}

export function updatePlacementPrizeDraft(drafts = {}, placement, patch = {}) {
  const key = String(placement);
  return {
    ...drafts,
    [key]: {
      ...(drafts?.[key] || EMPTY_PLACEMENT_PRIZE),
      ...patch,
    },
  };
}

export function getPlacementPrizeLabel(prize = {}) {
  const type = String(prize.type || "").trim().toUpperCase();
  if (type === STRUCTURED_PRIZE_TYPES.PAYOUT) {
    const amount = Number(prize.amount);
    if (!Number.isFinite(amount) || amount <= 0) return "";
    const rounded = Math.round(amount * 100) / 100;
    const text = Number.isInteger(rounded)
      ? String(rounded)
      : rounded.toFixed(2).replace(/\.?0+$/, "");
    return `${text} €`;
  }
  if (type === STRUCTURED_PRIZE_TYPES.PHYSICAL) {
    return normalizePrizeText(prize.name);
  }
  return "";
}

export function buildStructuredPlacementPrizes(drafts = {}, options = {}) {
  const normalized = normalizePlacementPrizeDrafts(drafts, options);
  const result = {};

  SUPPORTED_STRUCTURED_PLACEMENTS.forEach((placement) => {
    const prize = normalized[placement];
    if (!prize?.enabled) return;
    if (placement === "3" && !options.thirdPlaceEnabled) return;
    if (placement === "4" && (!options.thirdPlaceEnabled || !options.fourthPlaceEnabled)) return;

    const type = String(prize.type || STRUCTURED_PRIZE_TYPES.PAYOUT).trim().toUpperCase();
    if (type === STRUCTURED_PRIZE_TYPES.PAYOUT) {
      const amount = Number(prize.amount);
      if (!Number.isFinite(amount) || amount < 1 || amount > 10000) {
        throw new Error("Bitte gib einen Auszahlungsbetrag zwischen 1 € und 10.000 € an.");
      }
      result[placement] = {
        enabled: true,
        type: STRUCTURED_PRIZE_TYPES.PAYOUT,
        amount,
        currency: "EUR",
        label: getPlacementPrizeLabel({ ...prize, type }),
      };
      return;
    }

    if (type === STRUCTURED_PRIZE_TYPES.PHYSICAL) {
      const name = normalizePrizeText(prize.name);
      if (!name) {
        throw new Error("Bitte gib für jeden Sachpreis einen Namen ein.");
      }
      const description = normalizePrizeText(prize.description);
      result[placement] = {
        enabled: true,
        type: STRUCTURED_PRIZE_TYPES.PHYSICAL,
        name,
        label: name,
        ...(description ? { description } : {}),
      };
      return;
    }

    throw new Error("Bitte wähle eine gültige Preisart aus.");
  });

  if (!result["1"]) {
    throw new Error("Bitte konfiguriere einen strukturierten Preis für Platz 1.");
  }
  if (result["3"] && !result["2"]) {
    throw new Error("Ein Preis für Platz 3 ist nur mit einem Preis für Platz 2 möglich.");
  }

  return result;
}

export function buildLegacyPrizesFromPlacementPrizes(placementPrizes = {}, { thirdPlaceEnabled = false } = {}) {
  return {
    first: getPlacementPrizeLabel(placementPrizes["1"]) || "",
    second: getPlacementPrizeLabel(placementPrizes["2"]) || "",
    third: thirdPlaceEnabled ? (getPlacementPrizeLabel(placementPrizes["3"]) || "") : "",
  };
}
